// Ported from prototype/src/data/seed.js (INTENT_MAP, KEYWORD_SUGGESTIONS) and prototype/src/agent.js
// (detectIntents), plus common Hindi / Hinglish words (LANE-D-3). Used for the deterministic keyword
// fallback (brief §3) and to widen matching so a
// client's everyday words ("wedding", "job") reach astrologers' keywords and speciality tags.

export interface IntentDefinition {
  words: string[];
  keyword: string;
  label: string;
}

export const INTENT_MAP: IntentDefinition[] = [
  {
    words: [
      'marriage',
      'married',
      'wedding',
      'spouse',
      'husband',
      'wife',
      'rishta',
      'shaadi',
      'shadi',
      'vivah',
      'vivaah',
      'pati',
      'patni',
      'शादी',
      'विवाह',
      'पति',
      'पत्नी',
      'रिश्ता',
    ],
    keyword: 'marriage problems',
    label: 'Marriage',
  },
  {
    words: ['delay', 'late marriage', 'not getting married'],
    keyword: 'delay in marriage',
    label: 'Delay in marriage',
  },
  {
    words: ['kundli', 'match', 'matching', 'gun milan'],
    keyword: 'kundli matching',
    label: 'Kundli matching',
  },
  {
    words: [
      'love',
      'boyfriend',
      'girlfriend',
      'breakup',
      'ex ',
      'partner',
      'relationship',
      'pyar',
      'pyaar',
      'prem',
      'प्यार',
      'प्रेम',
    ],
    keyword: 'love problems',
    label: 'Love & relationships',
  },
  {
    words: ['divorce', 'separation', 'talaq', 'talak', 'तलाक'],
    keyword: 'divorce problems',
    label: 'Divorce',
  },
  {
    words: [
      'career',
      'job',
      'promotion',
      'interview',
      'work',
      'boss',
      'layoff',
      'naukri',
      'nokri',
      'नौकरी',
      'करियर',
    ],
    keyword: 'career guidance',
    label: 'Career',
  },
  {
    words: [
      'business',
      'shop',
      'startup',
      'sales',
      'customers',
      'vyapar',
      'vyaapar',
      'dhanda',
      'dukaan',
      'व्यापार',
      'व्यवसाय',
    ],
    keyword: 'business problems',
    label: 'Business',
  },
  {
    words: [
      'money',
      'debt',
      'loan',
      'financial',
      'finance',
      'loss',
      'paisa',
      'paise',
      'karz',
      'karza',
      'पैसा',
      'कर्ज',
      'क़र्ज़',
    ],
    keyword: 'financial problems',
    label: 'Finance',
  },
  {
    words: [
      'health',
      'illness',
      'sick',
      'disease',
      'hospital',
      'sehat',
      'bimari',
      'beemari',
      'स्वास्थ्य',
      'बीमारी',
    ],
    keyword: 'health problems',
    label: 'Health',
  },
  {
    words: [
      'study',
      'exam',
      'education',
      'college',
      'student',
      'padhai',
      'pariksha',
      'पढ़ाई',
      'परीक्षा',
    ],
    keyword: 'education problems',
    label: 'Education',
  },
  {
    words: ['abroad', 'visa', 'foreign', 'immigration', 'overseas', 'settle', 'videsh', 'विदेश'],
    keyword: 'foreign settlement',
    label: 'Foreign settlement',
  },
  {
    words: ['child', 'baby', 'pregnan', 'conceive', 'santan', 'santaan', 'संतान'],
    keyword: 'childbirth problems',
    label: 'Childbirth',
  },
  {
    words: ['family', 'in-laws', 'parents', 'siblings', 'parivar', 'parivaar', 'saas', 'परिवार'],
    keyword: 'family disputes',
    label: 'Family',
  },
  {
    words: ['property', 'land', 'house dispute'],
    keyword: 'property disputes',
    label: 'Property',
  },
  {
    words: ['vastu', 'home energy', 'office layout'],
    keyword: 'vastu consultation',
    label: 'Vastu',
  },
  { words: ['manglik', 'mangal'], keyword: 'manglik dosha', label: 'Manglik dosha' },
  {
    words: ['sade sati', 'shani', 'saturn'],
    keyword: 'shani sade sati',
    label: 'Shani / Sade Sati',
  },
];

export const KEYWORD_SUGGESTIONS = [
  'marriage problems',
  'marriage matching',
  'kundli matching',
  'love problems',
  'relationship issues',
  'divorce problems',
  'delay in marriage',
  'career guidance',
  'job problems',
  'business problems',
  'business growth',
  'financial problems',
  'debt problems',
  'health problems',
  'education problems',
  'foreign settlement',
  'visa problems',
  'childbirth problems',
  'family disputes',
  'property disputes',
  'court case problems',
  'vastu consultation',
  'numerology',
  'palmistry',
  'tarot reading',
  'horoscope reading',
  'birth chart analysis',
  'gemstone recommendation',
  'manglik dosha',
  'kaal sarp dosh',
  'shani sade sati',
  'muhurat',
  'name correction',
  'evil eye removal',
];

// Quick replies the Rishi offers when the topic is unclear (prototype Home.jsx).
export const TOPIC_QUICK_REPLIES = [
  'Marriage',
  'Relationship',
  'Career',
  'Business',
  'Health',
  'Family',
];

export interface DetectedIntent {
  keyword: string;
  label: string;
}

// Letters (any script, with their combining marks — Devanagari vowel signs are marks) and digits;
// everything else is a word break. Hindi and Hinglish work in the keyword fallback (LANE-D-3).
function wordsOnly(s: string): string {
  return s.toLowerCase().replace(/[^\p{L}\p{M}\p{N}]+/gu, ' ');
}

// Words match at a word start ("pregnan" finds "pregnancy", "match" doesn't fire inside
// "unmatched"); a word written with a trailing space ("ex ") must match as a whole word.
// "not the job" / "not about money" is the person contrasting away from a topic they already
// named, so it doesn't count (LANE-D-25) — but only that shape. A negator sitting directly next
// to the word ("not married yet", "not getting job", "not getting pregnant") is the person
// describing their problem as a negative state, which is still the topic (LANE-D-30): "no job"
// and "job is not going well" always counted, and pass 3 found the same was true one hop further
// away, so the rule now needs the connector word in between, not just any word.
const NEGATORS = new Set(['not', 'nahi', 'nahin']);
const NEGATION_CONNECTORS = new Set(['the', 'about', 'ka', 'ki', 'ke']);
function mentions(paddedText: string, word: string): boolean {
  const whole = word.endsWith(' ');
  const w = wordsOnly(word).trim();
  if (!w) return false;
  const needle = ` ${w}${whole ? ' ' : ''}`;
  for (let at = paddedText.indexOf(needle); at !== -1; at = paddedText.indexOf(needle, at + 1)) {
    const before = paddedText.slice(0, at).trim().split(' ').slice(-2);
    const [two, one] = before;
    const ruledOut =
      before.length === 2 && NEGATORS.has(two ?? '') && NEGATION_CONNECTORS.has(one ?? '');
    if (!ruledOut) return true;
  }
  return false;
}

// Deterministic: which INTENT_MAP entries does this text mention? De-duplicated by keyword.
export function detectIntents(text: string): DetectedIntent[] {
  const t = ` ${wordsOnly(text)} `;
  const seen = new Set<string>();
  const hits: DetectedIntent[] = [];
  for (const intent of INTENT_MAP) {
    if (seen.has(intent.keyword)) continue;
    if (intent.words.some((w) => mentions(t, w))) {
      seen.add(intent.keyword);
      hits.push({ keyword: intent.keyword, label: intent.label });
    }
  }
  return hits;
}

export function normalizeTerm(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

// Given intents from the AI or the keyword fallback (free words like "career", or exact keywords
// like "career guidance"), return every normalized term that should match a bid keyword or a
// speciality tag: the term itself plus, for each INTENT_MAP entry it belongs to, that entry's
// keyword and label. Keeps matching generous without the AI having to guess our exact spellings.
export function expandIntentTerms(intents: string[]): string[] {
  const out = new Set<string>();
  for (const raw of intents) {
    const term = normalizeTerm(raw);
    if (!term) continue;
    out.add(term);
    for (const intent of INTENT_MAP) {
      const matches =
        normalizeTerm(intent.keyword) === term ||
        normalizeTerm(intent.label) === term ||
        intent.words.some((w) => normalizeTerm(w) === term) ||
        ` ${term} `.includes(` ${normalizeTerm(intent.label)} `);
      if (matches) {
        out.add(normalizeTerm(intent.keyword));
        out.add(normalizeTerm(intent.label));
      }
    }
  }
  return [...out];
}

// Human-readable labels for the "Understood as:" chips — INTENT_MAP labels where known, otherwise
// the term itself with a capital first letter.
export function intentLabels(intents: string[]): string[] {
  const labels: string[] = [];
  for (const raw of intents) {
    const term = normalizeTerm(raw);
    const known = INTENT_MAP.find(
      (i) =>
        normalizeTerm(i.keyword) === term ||
        normalizeTerm(i.label) === term ||
        i.words.some((w) => normalizeTerm(w) === term),
    );
    const label = known ? known.label : term.charAt(0).toUpperCase() + term.slice(1);
    if (label && !labels.includes(label)) labels.push(label);
  }
  return labels;
}
